import axios from 'axios'
import { ensureCsrfToken } from '../../../lib/csrf'

const url = (programReference) =>
    `/api/digital-risk/benefits-governance/programs/${encodeURIComponent(programReference)}/`

const observedOutcomeCollectionUrl = (programReference) => `${url(programReference)}observed-outcomes/`
const observedOutcomeUrl = (reference) =>
    `/api/digital-risk/benefits-governance/observed-outcomes/${encodeURIComponent(reference)}/`
const realizedBenefitEvidenceCollectionUrl = (programReference) =>
    `${url(programReference)}realized-benefit-evidence/`
const realizedBenefitEvidenceUrl = (reference) =>
    `/api/digital-risk/benefits-governance/realized-benefit-evidence/${encodeURIComponent(reference)}/`
const valueRealizationCollectionUrl = (programReference) =>
    `${url(programReference)}value-realization-assessments/`
const valueRealizationUrl = (reference) =>
    `/api/digital-risk/benefits-governance/value-realization-assessments/${encodeURIComponent(reference)}/`

export const listValueRealizationAssessments = async ({ programReference }) => {
    const { data } = await axios.get(valueRealizationCollectionUrl(programReference), { withCredentials: true })
    return data
}
export const createValueRealizationAssessment = async ({ programReference, payload }) => {
    const { data } = await axios.post(valueRealizationCollectionUrl(programReference), payload, {
        withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}
export const getValueRealizationAssessment = async ({ reference }) => {
    const { data } = await axios.get(valueRealizationUrl(reference), { withCredentials: true })
    return data
}
export const updateValueRealizationAssessment = async ({ reference, payload }) => {
    const { data } = await axios.patch(valueRealizationUrl(reference), payload, {
        withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}
export const deleteValueRealizationAssessment = async ({ reference }) => {
    await axios.delete(valueRealizationUrl(reference), {
        withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
}
export const reviewValueRealizationAssessment = async ({ reference }) => {
    const { data } = await axios.post(`${valueRealizationUrl(reference)}review/`, {}, {
        withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}
export const getValueRealizationReadiness = async ({ programReference }) => {
    const { data } = await axios.get(`${url(programReference)}value-realization-readiness/`, { withCredentials: true })
    return data
}
export const getValueEvidenceMetrics = async ({ programReference, dimension, evidenceSources, periodStart, periodEnd }) => {
    const params = evidenceSources === undefined ? undefined : {
        dimension,
        selected_evidence: JSON.stringify(evidenceSources.map(({ source_type, source_reference }) => ({ source_type, source_reference }))),
        ...(periodStart ? { period_start: periodStart } : {}),
        ...(periodEnd ? { period_end: periodEnd } : {})
    }
    const { data } = await axios.get(`${url(programReference)}value-evidence-metrics/`, {
        withCredentials: true, ...(params ? { params } : {})
    })
    return data
}

export const fetchRealizedBenefitEvidence = async ({ programReference }) => {
    const { data } = await axios.get(realizedBenefitEvidenceCollectionUrl(programReference), {
        withCredentials: true
    })
    return data
}

export const createRealizedBenefitEvidence = async ({ programReference, payload }) => {
    const { data } = await axios.post(realizedBenefitEvidenceCollectionUrl(programReference), payload, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const fetchRealizedBenefitEvidenceRecord = async ({ reference }) => {
    const { data } = await axios.get(realizedBenefitEvidenceUrl(reference), { withCredentials: true })
    return data
}

export const updateRealizedBenefitEvidence = async ({ reference, payload }) => {
    const { data } = await axios.patch(realizedBenefitEvidenceUrl(reference), payload, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const deleteRealizedBenefitEvidence = async ({ reference }) => {
    await axios.delete(realizedBenefitEvidenceUrl(reference), {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
}

export const reviewRealizedBenefitEvidence = async ({ reference }) => {
    const { data } = await axios.post(`${realizedBenefitEvidenceUrl(reference)}review/`, {}, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const fetchObservedOutcomes = async ({ programReference, filters = {} }) => {
    const params = {}
    ;['expected_outcome_reference', 'program_project_reference', 'status'].forEach((key) => {
        if (filters[key] != null && filters[key] !== '') params[key] = filters[key]
    })
    const { data } = await axios.get(observedOutcomeCollectionUrl(programReference), {
        withCredentials: true,
        ...(Object.keys(params).length ? { params } : {})
    })
    return data
}

export const createObservedOutcome = async ({ programReference, payload }) => {
    const { data } = await axios.post(observedOutcomeCollectionUrl(programReference), payload, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const fetchObservedOutcome = async ({ reference }) => {
    const { data } = await axios.get(observedOutcomeUrl(reference), { withCredentials: true })
    return data
}

export const updateObservedOutcome = async ({ reference, payload }) => {
    const { data } = await axios.patch(observedOutcomeUrl(reference), payload, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const deleteObservedOutcome = async ({ reference }) => {
    await axios.delete(observedOutcomeUrl(reference), {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
}

export const reviewObservedOutcome = async ({ reference }) => {
    const { data } = await axios.post(`${observedOutcomeUrl(reference)}review/`, {}, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const recordOutcomeJudgment = async ({ reference, payload }) => {
    const { data } = await axios.post(`/api/digital-risk/benefits-governance/outcome-evaluations/${encodeURIComponent(reference)}/judgment/`, payload, {
        withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const fetchBenefitsGovernanceOverview = async () => {
    const { data } = await axios.get('/api/digital-risk/benefits-governance/programs/', { withCredentials: true })
    return data
}

export const fetchBenefitsGovernance = async ({ programReference }) => {
    const { data } = await axios.get(url(programReference), { withCredentials: true })
    return data
}

export const fetchEligibleCurrentEvidence = async ({ programReference }) => {
    const { data } = await axios.get(`${reassessmentUrl(programReference)}eligible-current-evidence/`, {
        withCredentials: true
    })
    return Array.isArray(data) ? data : []
}
export const fetchEligibleValueRealizationEvidence = async ({ programReference }) => {
    const { data } = await axios.get(
        `${reassessmentUrl(programReference)}eligible-value-realization-evidence/`,
        { withCredentials: true }
    )
    return Array.isArray(data) ? data : []
}

export const fetchPortfolioSnapshotHistory = async () => {
    const { data } = await axios.get('/api/digital-risk/dashboard/portfolio-snapshot-history/?scope=all', {
        withCredentials: true
    })
    return Array.isArray(data) ? data : []
}

export const approvePortfolioSnapshot = async ({ snapshotId, comments = '' }) => {
    const csrfToken = await ensureCsrfToken()
    const { data } = await axios.post(
        `/api/digital-risk/dashboard/approve-portfolio-snapshot/${snapshotId}/`,
        { comments },
        { withCredentials: true, headers: { 'X-CSRFToken': csrfToken } }
    )
    return data
}

export const updateBenefitsAssessment = async ({ programReference, payload }) => {
    const { data } = await axios.patch(url(programReference), payload, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

const reassessmentUrl = (programReference) =>
    `/api/digital-risk/portfolio-reassessments/programs/${encodeURIComponent(programReference)}/`
export { createPortfolioReassessment } from './reassessmentCreationApi'
export const updatePortfolioReassessment = async ({ reference, payload }) => {
    const { data } = await axios.patch(`/api/digital-risk/portfolio-reassessments/${reference}/`, payload, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}
export const capturePortfolioReassessment = async ({ reference, sourceId, selectedAnalyticalRole }) => {
    const { data } = await axios.post(
        `/api/digital-risk/portfolio-reassessments/${reference}/capture/`,
        { current_source_type: 'APPROVED_PORTFOLIO_SNAPSHOT', current_source_id: sourceId, ...(selectedAnalyticalRole ? { selected_analytical_role: selectedAnalyticalRole } : {}) },
        { withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() } }
    )
    return data
}
export const completePortfolioReassessment = async ({ reference, payload = {} }) => {
    const { data } = await axios.post(
        `/api/digital-risk/portfolio-reassessments/${reference}/complete/`,
        payload,
        { withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() } }
    )
    return data
}

export const prepareProgramReauthorization = async ({ programReference, reassessmentReference }) => {
    const { data } = await axios.post(
        `/api/digital-risk/program-reauthorizations/programs/${encodeURIComponent(programReference)}/reassessments/${encodeURIComponent(reassessmentReference)}/prepare/`,
        {},
        { withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() } }
    )
    return data
}

export const updateProgramReauthorization = async ({ reference, payload }) => {
    const { data } = await axios.patch(`/api/digital-risk/program-reauthorizations/${reference}/`, payload, {
        withCredentials: true,
        headers: { 'X-CSRFToken': await ensureCsrfToken() }
    })
    return data
}

export const submitProgramReauthorization = async ({ reference }) => {
    const { data } = await axios.post(
        `/api/digital-risk/program-reauthorizations/${reference}/submit/`,
        {},
        { withCredentials: true, headers: { 'X-CSRFToken': await ensureCsrfToken() } }
    )
    return data
}

export const fetchProgramReauthorization = async ({ reference }) => {
    const { data } = await axios.get(`/api/digital-risk/program-reauthorizations/${encodeURIComponent(reference)}/`, { withCredentials: true })
    return data
}

export const fetchLegacyBaselineCandidate = async ({ programReference, authorizationReference }) => {
    const { data } = await axios.get(`${reassessmentUrl(programReference)}legacy-baseline/${encodeURIComponent(authorizationReference)}/`, { withCredentials: true })
    return data
}
